// =============================================================================
// TopUpTaskListPage.jsx — the Stock Top-up approval queue (stock-top-ups plan §4).
// =============================================================================
// Its OWN assignable task queue, isolated from the Responders + Pickers lists,
// backed by `top_up_approval` rows (one per submitted top-up) AND `top_up_comment`
// rows (a DFV-Service comment on a top-up, routed here not to Responders — 2026-3076).
// Styled + functioning like the Responders Task List (John, 12 Aug 2026): stat cards,
// search/status/state/staff filters, and per-task Assign / Release / Review. A request
// can now show its approval row plus one row per comment. "Review" opens the tabbed
// approval view (its Comments panel shows the thread); Confirm & Send there Actions the
// APPROVAL task, while a `top_up_comment` row is cleared by its own "✓ Actioned" button.
// Reference: docs/stock-top-ups-plan.md §H2; docs/2026-3076-comment-routing-plan.md.
// =============================================================================
import React from "react";
import { isAdminRole } from "../../app/access";
import { useRequests } from "../../app/RequestsContext.jsx";
import { useSession } from "../../app/SessionContext.jsx";
import { listAssignableStaff } from "../../data/profiles";
import { insertInternalNote } from "../../data/requestInternalNotes";
import {
  listTopUpApprovalTasks,
  TOP_UP_TASK_TYPES,
  updateTaskByIdReturning,
} from "../../data/requestTasks";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { useLiveList } from "../../hooks/useLiveList";
import { sendEmail } from "../../utils/actions";
import { logAudit } from "../../utils/auditLog";
import { fmtDateTimeLocal, formatRef } from "../../utils/helpers";
import { ModalBackdrop } from "../form/FormUI.jsx";
import TopUpApprovalView from "./TopUpApprovalView.jsx";

const selStyle = "py-[6px] px-[10px] rounded-[6px] border border-[#ccc] text-[13px] bg-white";
const thStyle =
  "px-[10px] py-2 text-left text-[11px] font-bold uppercase tracking-[0.5px] text-[#607D79] border-b-2 border-[#E0E0E0] whitespace-nowrap bg-[#FAFAFA]";
const tdStyle = "px-[10px] py-[7px] text-[13px] border-b border-[#F0F0F0] align-middle";

const btn = (bg, border, colour) => ({
  fontSize: 11,
  padding: "3px 9px",
  borderRadius: 5,
  whiteSpace: "nowrap",
  background: bg,
  border: `1px solid ${border}`,
  color: colour,
  fontWeight: 600,
  cursor: "pointer",
});

export default function TopUpTaskListPage() {
  const { user } = useSession();
  const { loadTopUpTasks } = useRequests();
  const isSupervisor = isAdminRole(user);
  const [selectedId, setSelectedId] = React.useState(null);
  // The task LIST is live (C3): Realtime on request_tasks (top_up_approval rows) with
  // delta-apply + fallback poll + reconnect. Suspended while a review is open.
  const {
    rows: tasks,
    loading,
    refresh,
    applyChange,
  } = useLiveList({
    table: "request_tasks",
    fetch: listTopUpApprovalTasks,
    rowBelongs: function (t) {
      // The Top-up queue = approval tasks AND top_up_comment tasks (2026-3076).
      return TOP_UP_TASK_TYPES.includes(t.type);
    },
    sort: function (a, b) {
      // Newest first — matches listTopUpApprovalTasks' created_at DESC.
      return (b.created_at || "").localeCompare(a.created_at || "");
    },
    enabled: selectedId == null,
  });
  const [assignableStaff, setAssignableStaff] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("unactioned");
  const [filterState, setFilterState] = React.useState("all");
  const [filterStaff, setFilterStaff] = React.useState("all");
  const [assignTask, setAssignTask] = React.useState(null);
  const [assignTarget, setAssignTarget] = React.useState("");
  const [assignNote, setAssignNote] = React.useState("");
  const [releaseTask, setReleaseTask] = React.useState(null);
  const [releaseNote, setReleaseNote] = React.useState("");
  const assignNoteId = React.useId();
  const releaseNoteId = React.useId();

  // Staff roster for the Assign modal, loaded once. Each entry is the identity
  // triple { id, display_name, staff_user_email } so the assign action carries the
  // profile ID (never a mutable display string) and resolves the recipient email
  // from it — see docs/assignee-email-by-id-plan.md. Already name-ordered by the query.
  React.useEffect(function () {
    listAssignableStaff().then(function (rows) {
      setAssignableStaff(rows || []);
    });
  }, []);

  // Slim badge poll (C3, pragmatic): the LIST is live via useLiveList; this poll exists
  // ONLY to keep the nav badge count fresh. Deduped in usePortalData; guarded when the
  // requests context isn't provided.
  useAutoRefresh(function () {
    if (loadTopUpTasks) loadTopUpTasks();
  });

  function patchTask(row) {
    if (!row) return;
    // Optimistic patch via the same delta path a Realtime UPDATE uses (idempotent). (C3)
    applyChange({ eventType: "UPDATE", new: row });
  }

  async function takeOn(task) {
    const now = new Date().toISOString();
    const { data, error } = await updateTaskByIdReturning(task.id, {
      // assigned_to (text) is filled server-side by trg_sync_request_task_assignee_name
      // (db-202) from assigned_to_id's profile — the client no longer sends the name.
      assigned_to_id: user.id,
      assigned_at: now,
    });
    if (!error) {
      patchTask(data);
      logAudit(
        "task_taken_on",
        { request_id: task.request_id, ref: task.ref_no, by: user.shelterName },
        task.request_id
      );
      setSelectedId(task.request_id); // Take On also opens the approval.
    }
  }

  async function assignTo(task, profileId, note) {
    const now = new Date().toISOString();
    // Resolve the assignee from the loaded roster BY ID — never by display name
    // (see docs/assignee-email-by-id-plan.md). Keying on the id guarantees
    // assigned_to_id is set and the notification email reaches the right address,
    // regardless of what the on-screen name is.
    const assignee = assignableStaff.find(function (s) {
      return s.id === profileId;
    });
    const assigneeName = assignee?.display_name || "";
    const assigneeEmail = assignee?.staff_user_email;
    const { data, error } = await updateTaskByIdReturning(task.id, {
      // assigned_to (text) is derived server-side from assigned_to_id (db-202);
      // assigneeName is still used below for the audit/note/email only.
      assigned_to_id: profileId,
      assigned_at: now,
    });
    if (!error) {
      patchTask(data);
      logAudit(
        "task_assigned",
        {
          request_id: task.request_id,
          ref: task.ref_no,
          to: assigneeName,
          by: user.shelterName,
          note,
        },
        task.request_id
      );
      if ((note || "").trim()) {
        insertInternalNote({
          requestId: task.request_id,
          author: user.shelterName,
          authorId: user.id || null,
          text: `Assigned to ${assigneeName}: ${note.trim()}`,
          kind: "assign",
        });
      }
      if (assigneeEmail) {
        sendEmail("task_assigned", {
          requestId: task.request_id,
          assigneeName: assigneeName,
          assigneeEmail: assigneeEmail,
          refNo: task.ref_no || "",
          shelterName: task.shelter_name || "",
          taskType: "Stock Top-up",
          assignedBy: user.shelterName,
          note: note || "",
        });
      }
    }
    setAssignTask(null);
    setAssignTarget("");
    setAssignNote("");
  }

  async function release(task, note) {
    const { data, error } = await updateTaskByIdReturning(task.id, {
      // Nulling assigned_to_id clears assigned_to (text) via trg_sync_request_task_assignee_name (db-202).
      assigned_to_id: null,
      assigned_at: null,
      release_note: note || null,
    });
    if (!error) {
      patchTask(data);
      logAudit(
        "task_released",
        { request_id: task.request_id, ref: task.ref_no, by: user.shelterName, note },
        task.request_id
      );
    }
    setReleaseTask(null);
    setReleaseNote("");
  }

  // Mark a top_up_comment task Actioned (clears it from the Top-up queue). Comment tasks
  // have no Confirm & Send, so this is their completion path. Gated in the UI to the
  // assignee / an unassigned task / a supervisor so the db-60 guard never rejects it.
  async function actionComment(task) {
    const { data, error } = await updateTaskByIdReturning(task.id, {
      completed_at: new Date().toISOString(),
    });
    if (!error) {
      patchTask(data);
      logAudit(
        "task_actioned",
        { request_id: task.request_id, ref: task.ref_no, by: user.shelterName },
        task.request_id
      );
    }
  }

  function fmtDate(iso) {
    if (!iso) return "-";
    return fmtDateTimeLocal(iso) || "-";
  }

  // Review opens the tabbed approval view; Back / Confirm returns + reloads.
  if (selectedId != null) {
    return (
      <TopUpApprovalView
        requestId={selectedId}
        onDone={function () {
          // Clearing selectedId re-enables useLiveList (enabled: selectedId == null),
          // which re-fetches the list — no explicit reload needed.
          setSelectedId(null);
        }}
      />
    );
  }

  const list = tasks || [];
  const unactioned = list.filter((t) => !t.completed_at).length;
  const unassigned = list.filter((t) => !t.assigned_to && !t.completed_at).length;
  const actioned = list.filter((t) => !!t.completed_at).length;
  const assignedAll = list.filter((t) => !!t.assigned_to && !t.completed_at).length;
  // "Mine" strictly by assignee id — assigned_to_id is the enforced source of truth
  // (db-201 FK + db-202 name-sync trigger; assigned_to text is a derived cache). The
  // old `|| t.assigned_to === user.shelterName` name fallback was removed with T2a.
  const isMineTask = (t) => !!t.assigned_to_id && t.assigned_to_id === user.id;
  const assignedMe = list.filter((t) => !t.completed_at && isMineTask(t)).length;

  const stateList = Array.from(new Set(list.filter((t) => t.state).map((t) => t.state))).sort();
  const staffList = Array.from(
    new Set(list.filter((t) => t.assigned_to).map((t) => t.assigned_to))
  ).sort();

  const filtered = list.filter((t) => {
    if (filterState !== "all" && t.state !== filterState) return false;
    if (filterStaff !== "all" && t.assigned_to !== filterStaff) return false;
    if (filterStatus === "unactioned" && t.completed_at) return false;
    if (filterStatus === "actioned" && !t.completed_at) return false;
    if (filterStatus === "assigned" && (!t.assigned_to || t.completed_at)) return false;
    if (filterStatus === "unassigned" && (t.assigned_to || t.completed_at)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (
        !(t.ref_no || "").toLowerCase().includes(q) &&
        !(t.shelter_name || "").toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  return (
    <div className="px-7 py-6 max-w-[var(--page-max)] mx-auto">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: "#00695C", fontSize: 22 }}>Top-up Task List</h2>
          <div style={{ fontSize: 13, color: "#607D79", marginTop: 2 }}>
            Stock Top-up requests awaiting approval
          </div>
        </div>
        <button
          onClick={function () {
            refresh();
          }}
          style={{
            background: "white",
            border: "1px solid #ccc",
            borderRadius: 7,
            padding: "7px 14px",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Stats */}
      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 16, marginBottom: 20 }}
      >
        {[
          { label: "Unassigned", value: unassigned, colour: "#E53935" },
          { label: "Assigned ME", value: assignedMe, colour: "#5E35B1" },
          { label: "Assigned All", value: assignedAll, colour: "#1E88E5" },
          { label: "Unactioned", value: unactioned, colour: "#FFB300" },
          { label: "Actioned", value: actioned, colour: "#00897B" },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              background: "white",
              borderRadius: 10,
              padding: "10px 20px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
            >
              <div style={{ fontSize: 36, fontWeight: 700, color: s.colour }}>{s.value}</div>
              <div style={{ fontSize: 20, color: "#607D79" }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div
        style={{
          background: "white",
          borderRadius: 10,
          padding: "14px 16px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
          marginBottom: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "center",
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ref, DFV Service..."
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ccc",
            fontSize: 13,
            minWidth: 220,
          }}
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className={selStyle}
        >
          <option value="all">All statuses</option>
          <option value="unactioned">Unactioned</option>
          <option value="unassigned">Unassigned</option>
          <option value="assigned">Assigned</option>
          <option value="actioned">Actioned</option>
        </select>
        <select
          value={filterState}
          onChange={(e) => setFilterState(e.target.value)}
          className={selStyle}
        >
          <option value="all">All states</option>
          {stateList.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={filterStaff}
          onChange={(e) => setFilterStaff(e.target.value)}
          className={selStyle}
        >
          <option value="all">All staff</option>
          {staffList.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: "#607D79", marginLeft: "auto" }}>
          {filtered.length} task{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table */}
      <div
        style={{
          background: "white",
          borderRadius: 10,
          boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#607D79" }}>Loading tasks...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#607D79" }}>
            {filterStatus === "unactioned"
              ? "🎉 No unactioned top-ups!"
              : "No tasks match your filters."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#FAFAFA" }}>
                  <th className={thStyle}>Ref</th>
                  <th className={thStyle}>DFV Service /Code</th>
                  <th className={thStyle}>State</th>
                  <th className={thStyle}>Created</th>
                  <th className={thStyle}>Assigned To</th>
                  <th className={thStyle}>Actioned</th>
                  <th className={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((task) => {
                  const isCompleted = !!task.completed_at;
                  const isMine = isMineTask(task);
                  const assignedToOther = task.assigned_to && !isMine;
                  const canRelease = isSupervisor || isMine;
                  const canTakeOver = isSupervisor || !assignedToOther;
                  const isComment = task.type === "top_up_comment";
                  // Actioned mirrors the Responders rule: the assignee, an UNASSIGNED
                  // task, or a supervisor may clear it (all pass the db-60 guard).
                  const canActioned = isSupervisor || isMine || !task.assigned_to_id;
                  return (
                    <tr key={task.id} style={{ opacity: isCompleted ? 0.5 : 1 }}>
                      <td className={tdStyle}>
                        <button
                          onClick={() => setSelectedId(task.request_id)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#00897B",
                            fontWeight: 700,
                            cursor: "pointer",
                            padding: 0,
                            fontSize: 13,
                            textDecoration: "underline",
                          }}
                        >
                          {formatRef(task.ref_no)}
                        </button>
                        {isComment && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 10,
                              fontWeight: 700,
                              padding: "1px 6px",
                              borderRadius: 4,
                              background: "#E3F2FD",
                              color: "#1565C0",
                              border: "1px solid #90CAF9",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Comment
                          </span>
                        )}
                      </td>
                      <td className={tdStyle}>
                        <span style={{ fontWeight: 600 }}>{task.shelter_name || "-"}</span>
                        {task.shelter_code && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#00695C",
                              fontWeight: 600,
                              marginTop: 2,
                            }}
                          >
                            {task.shelter_code}
                          </div>
                        )}
                      </td>
                      <td className={tdStyle}>{task.state || "-"}</td>
                      <td className={tdStyle}>{fmtDate(task.created_at)}</td>
                      <td className={tdStyle}>
                        {task.assigned_to ? (
                          <span
                            style={{
                              fontWeight: isMine ? 700 : 400,
                              color: isMine ? "#00695C" : "inherit",
                            }}
                          >
                            {task.assigned_to}
                            {isMine ? " (you)" : ""}
                          </span>
                        ) : (
                          <span style={{ color: "#607D79", fontStyle: "italic" }}>Unassigned</span>
                        )}
                      </td>
                      <td className={tdStyle}>{fmtDate(task.completed_at)}</td>
                      <td className={tdStyle}>
                        {!isCompleted && (
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              alignItems: "center",
                              flexWrap: "wrap",
                              rowGap: 4,
                            }}
                          >
                            {!task.assigned_to && (
                              <button
                                onClick={() => takeOn(task)}
                                disabled={!canTakeOver}
                                style={{
                                  ...btn("#E0F2F1", "#00897B40", "#00695C"),
                                  opacity: canTakeOver ? 1 : 0.45,
                                  cursor: canTakeOver ? "pointer" : "not-allowed",
                                }}
                              >
                                Assign Me
                              </button>
                            )}
                            {task.assigned_to && (
                              <button
                                onClick={() => {
                                  setReleaseTask(task);
                                  setReleaseNote("");
                                }}
                                disabled={!canRelease}
                                style={{
                                  ...btn("#FFF3E0", "#FFCC80", "#E65100"),
                                  opacity: canRelease ? 1 : 0.45,
                                  cursor: canRelease ? "pointer" : "not-allowed",
                                }}
                              >
                                Release
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setAssignTask(task);
                                setAssignTarget("");
                                setAssignNote("");
                              }}
                              disabled={!canTakeOver}
                              style={{
                                ...btn("#EDE8FA", "#C4B5FD", "#534AB7"),
                                opacity: canTakeOver ? 1 : 0.45,
                                cursor: canTakeOver ? "pointer" : "not-allowed",
                              }}
                            >
                              Assign Other
                            </button>
                            <button
                              onClick={() => setSelectedId(task.request_id)}
                              style={btn("#E8F5E9", "#A5D6A7", "#2E7D32")}
                            >
                              Review
                            </button>
                            {isComment && (
                              <button
                                onClick={() => actionComment(task)}
                                disabled={!canActioned}
                                style={{
                                  ...btn("#E8F5E9", "#66BB6A", "#1B5E20"),
                                  opacity: canActioned ? 1 : 0.45,
                                  cursor: canActioned ? "pointer" : "not-allowed",
                                }}
                              >
                                ✓ Actioned
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Assign to modal */}
      {assignTask && (
        <ModalBackdrop
          onClose={() => setAssignTask(null)}
          contentStyle={{
            background: "white",
            borderRadius: 12,
            padding: 24,
            width: 360,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 15, color: "#00695C", marginBottom: 14 }}>
            Assign top-up {formatRef(assignTask.ref_no)}
          </div>
          <select
            value={assignTarget}
            onChange={(e) => setAssignTarget(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 7,
              border: "1px solid #CFE0DE",
              fontSize: 14,
              marginBottom: 14,
              fontFamily: "inherit",
            }}
          >
            <option value="">— Select person —</option>
            {assignableStaff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.display_name}
              </option>
            ))}
          </select>
          <label
            htmlFor={assignNoteId}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#1A2E2B",
              display: "block",
              marginBottom: 6,
            }}
          >
            Note for assignee (required, min 4 characters)
          </label>
          <textarea
            id={assignNoteId}
            value={assignNote}
            onChange={(e) => setAssignNote(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 7,
              border: "1px solid #CFE0DE",
              fontSize: 13,
              fontFamily: "inherit",
              resize: "vertical",
              minHeight: 70,
              boxSizing: "border-box",
              marginBottom: 14,
            }}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={() => setAssignTask(null)}
              style={{
                background: "#eee",
                border: "none",
                borderRadius: 6,
                padding: "7px 16px",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (assignTarget && assignNote.trim().length >= 4)
                  assignTo(assignTask, assignTarget, assignNote);
              }}
              disabled={!assignTarget || assignNote.trim().length < 4}
              style={{
                background: "#00897B",
                color: "white",
                border: "none",
                borderRadius: 6,
                padding: "7px 16px",
                cursor: assignTarget && assignNote.trim().length >= 4 ? "pointer" : "not-allowed",
                fontSize: 13,
                fontWeight: 600,
                opacity: assignTarget && assignNote.trim().length >= 4 ? 1 : 0.5,
              }}
            >
              Assign
            </button>
          </div>
        </ModalBackdrop>
      )}

      {/* Release modal */}
      {releaseTask && (
        <ModalBackdrop
          onClose={() => setReleaseTask(null)}
          contentStyle={{
            background: "white",
            borderRadius: 12,
            padding: 24,
            width: 400,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 15, color: "#00695C", marginBottom: 14 }}>
            Release top-up {formatRef(releaseTask.ref_no)}
          </div>
          <label
            htmlFor={releaseNoteId}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#1A2E2B",
              display: "block",
              marginBottom: 6,
            }}
          >
            Reason for releasing (required, min 4 characters)
          </label>
          <textarea
            id={releaseNoteId}
            value={releaseNote}
            onChange={(e) => setReleaseNote(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 7,
              border: "1px solid #CFE0DE",
              fontSize: 13,
              fontFamily: "inherit",
              resize: "vertical",
              minHeight: 70,
              boxSizing: "border-box",
              marginBottom: 14,
            }}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={() => setReleaseTask(null)}
              style={{
                background: "#eee",
                border: "none",
                borderRadius: 6,
                padding: "7px 16px",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (releaseNote.trim().length >= 4) release(releaseTask, releaseNote);
              }}
              disabled={releaseNote.trim().length < 4}
              style={{
                background: "#E65100",
                color: "white",
                border: "none",
                borderRadius: 6,
                padding: "7px 16px",
                cursor: releaseNote.trim().length >= 4 ? "pointer" : "not-allowed",
                fontSize: 13,
                fontWeight: 600,
                opacity: releaseNote.trim().length >= 4 ? 1 : 0.5,
              }}
            >
              Release
            </button>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}
